import type { SupabaseClient } from '@supabase/supabase-js';

const OTP_LENGTH = 6;

export function renderAuthForm(supabase: SupabaseClient, machineCode: string) {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <div class="card brand-card">
      <img class="logo" src="/hainsworth-logo.png" alt="Hainsworth Laundry">
      <p class="tagline">We've got the laundry covered.</p>
      <h1>Sign in to pay</h1>
      <p class="sub">Enter your email and we'll send a code.</p>
      <form id="email-form">
        <input type="email" id="email" placeholder="you@example.com" required autofocus autocomplete="email" inputmode="email">
        <button type="submit" class="primary" style="margin-top: 12px;">Send code</button>
        <div class="error" id="error"></div>
      </form>
    </div>
  `;

  document.getElementById('email-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = (document.getElementById('email') as HTMLInputElement).value.trim();
    const errEl = document.getElementById('error')!;
    errEl.textContent = '';

    const submitBtn = (e.target as HTMLFormElement).querySelector('button[type=submit]') as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending…';

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.href, shouldCreateUser: true },
    });
    if (error) {
      errEl.textContent = error.message;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send code';
      return;
    }
    renderOtpForm(supabase, email, machineCode);
  });
}

function renderOtpForm(supabase: SupabaseClient, email: string, _machineCode: string) {
  const app = document.getElementById('app')!;
  const cellsHtml = Array.from({ length: OTP_LENGTH }, (_, i) =>
    `<input class="otp-cell" type="text" inputmode="numeric" maxlength="1" autocomplete="one-time-code" data-i="${i}" ${i === 0 ? 'autofocus' : ''}>`
  ).join('');

  app.innerHTML = `
    <div class="card brand-card">
      <img class="logo" src="/hainsworth-logo.png" alt="Hainsworth Laundry">
      <h1>Check your email</h1>
      <p class="sub">We sent a code to <strong>${email}</strong>.</p>
      <form id="otp-form">
        <div class="otp-row" id="otp-row">${cellsHtml}</div>
        <button type="submit" class="primary" id="verify-btn" disabled>Verify</button>
        <div class="error" id="error"></div>
        <button type="button" id="resend" class="ghost" style="margin-top: 8px;">Resend code</button>
      </form>
    </div>
  `;

  const cells = Array.from(document.querySelectorAll<HTMLInputElement>('.otp-cell'));
  const verifyBtn = document.getElementById('verify-btn') as HTMLButtonElement;

  const code = () => cells.map(c => c.value).join('');
  const updateBtn = () => { verifyBtn.disabled = code().length !== OTP_LENGTH; };

  cells.forEach((cell, i) => {
    cell.addEventListener('input', () => {
      // Strip non-digits, keep only the last char so users can overwrite by typing
      cell.value = cell.value.replace(/\D/g, '').slice(-1);
      if (cell.value && i < OTP_LENGTH - 1) cells[i + 1].focus();
      updateBtn();
      // Auto-submit when fully filled
      if (code().length === OTP_LENGTH) verifyBtn.click();
    });
    cell.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !cell.value && i > 0) {
        cells[i - 1].focus();
        cells[i - 1].value = '';
        updateBtn();
        e.preventDefault();
      } else if (e.key === 'ArrowLeft' && i > 0) {
        cells[i - 1].focus(); e.preventDefault();
      } else if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) {
        cells[i + 1].focus(); e.preventDefault();
      }
    });
    cell.addEventListener('paste', (e) => {
      const txt = e.clipboardData?.getData('text') ?? '';
      const digits = txt.replace(/\D/g, '').slice(0, OTP_LENGTH);
      if (!digits) return;
      e.preventDefault();
      digits.split('').forEach((d, j) => { if (cells[j]) cells[j].value = d; });
      const next = Math.min(digits.length, OTP_LENGTH - 1);
      cells[next].focus();
      updateBtn();
      if (code().length === OTP_LENGTH) verifyBtn.click();
    });
  });

  document.getElementById('otp-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const token = code();
    if (token.length !== OTP_LENGTH) return;
    const errEl = document.getElementById('error')!;
    errEl.textContent = '';
    verifyBtn.disabled = true;
    verifyBtn.textContent = 'Verifying…';

    const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
    if (error) {
      errEl.textContent = error.message;
      verifyBtn.disabled = false;
      verifyBtn.textContent = 'Verify';
      cells.forEach(c => c.value = '');
      cells[0].focus();
      return;
    }
    window.location.reload();
  });

  document.getElementById('resend')!.addEventListener('click', async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.href, shouldCreateUser: true },
    });
    btn.textContent = 'Code sent — check your inbox';
    setTimeout(() => { btn.disabled = false; btn.textContent = 'Resend code'; }, 4000);
  });
}
